export function generateTestWords(allWords, testWordHistory, hardMode) {
  // Filter out objects from words that are already in testWordHistory
  const words = allWords.filter((word) => !testWordHistory.includes(word.word));

  let testWords = [];

  // Helper function to check if a word object is already in the testWords array
  function isWordIncluded(word) {
    return testWords.some((item) => item.word === word.word);
  }

  // Step 1: Select a random word from the words array
  let firstWord = words[Math.floor(Math.random() * words.length)];
  testWords.push(firstWord);

  // Step 2: Find a word that starts with the same letter as the first word, different category
  let filteredWords = words.filter(
    (w) =>
      w.word.charAt(0) === firstWord.word.charAt(0) &&
      w.category !== firstWord.category &&
      !isWordIncluded(w)
  );
  if (filteredWords.length > 0) {
    let secondWord =
      filteredWords[Math.floor(Math.random() * filteredWords.length)];
    testWords.push(secondWord);
  }

  // Step 3: Find a word that starts with a different letter than the first word, same category
  filteredWords = words.filter(
    (w) =>
      w.word.charAt(0) !== firstWord.word.charAt(0) &&
      w.category === firstWord.category &&
      !isWordIncluded(w)
  );
  if (filteredWords.length > 0) {
    let thirdWord =
      filteredWords[Math.floor(Math.random() * filteredWords.length)];
    testWords.push(thirdWord);
  }

  // Step 4: Find a word that starts with the same letter as the third word, same category as the second word
  if (testWords.length > 2) {
    let thirdWord = testWords[2];
    let secondWord = testWords[1];
    filteredWords = words.filter(
      (w) =>
        w.word.charAt(0) === thirdWord.word.charAt(0) &&
        w.category === secondWord.category &&
        !isWordIncluded(w)
    );
    if (filteredWords.length > 0) {
      let fourthWord =
        filteredWords[Math.floor(Math.random() * filteredWords.length)];
      testWords.push(fourthWord);
    }
  }

  return randomSortOrientation(testWords, hardMode);
}

// Function to randomly choose a sorting orientation
function randomSortOrientation(testWords, hardMode) {
  if (!hardMode) {
    return sortOrientation2(testWords); // To match the FTUE orientation
  }

  // Generate a random number (0 or 1)
  let randomChoice = Math.floor(Math.random() * 2);

  // Call the respective sorting function based on the random number
  if (randomChoice === 0) {
    return sortOrientation1(testWords);
  } else {
    return sortOrientation2(testWords);
  }
}

// Sorts testWords so that matching letters are in positions 0,1 and 2,3 (row 1 and 2)
function sortOrientation1(testWords) {
  // Group words by the first letter of the word property
  let grouped = {};
  testWords.forEach((word) => {
    let firstLetter = word.word.charAt(0);
    if (!grouped[firstLetter]) {
      grouped[firstLetter] = [];
    }
    grouped[firstLetter].push(word);
  });

  // Flatten groups into a single array with adjacent matching letters
  let sortedWords = [];
  Object.keys(grouped).forEach((key) => {
    sortedWords = sortedWords.concat(grouped[key]);
  });

  return sortedWords;
}

// Sorts testWords so that matching letters are in positions 0,2 and 1,3 (column 1 and 2)
function sortOrientation2(testWords) {
  // Group words by the first letter of the word property
  let grouped = {};
  testWords.forEach((word) => {
    let firstLetter = word.word.charAt(0);
    if (!grouped[firstLetter]) {
      grouped[firstLetter] = [];
    }
    grouped[firstLetter].push(word);
  });

  // Reorder to alternate groups
  let keys = Object.keys(grouped);
  let sortedWords = [];
  if (keys.length == 2) {
    // Assume each group has exactly two members
    sortedWords.push(
      grouped[keys[0]][0],
      grouped[keys[1]][0],
      grouped[keys[0]][1],
      grouped[keys[1]][1]
    );
  }

  return sortedWords;
}

// Function to randomly select a correct word from the testWords array
export function selectCorrectWord(testWords, hardMode) {
  if (testWords.length === 0) {
    return null; // Return null or handle the empty array case as appropriate
  }

  if (hardMode) {
    let randomIndex = Math.floor(Math.random() * testWords.length);
    return testWords[randomIndex].word;
  }

  return testWords[testWords.length - 1].word;
}

export function generateOptionWords(testWords, correctWord, wordBank) {
  let optionWords = [];

  // 1. Add the correct word first
  const correctWordObj = testWords.find((w) => w.word === correctWord);
  optionWords.push(correctWordObj);

  // Helper to check if the word is already used in testWords
  function isWordUsed(word) {
    return testWords.some((w) => w.word === word);
  }

  // Filter out any words already used in testWords
  let availableWords = wordBank.filter((w) => !isWordUsed(w.word));

  // 2. Find an option that starts with the same letter as correctWord and different category
  let sameLetterDifferentCategory = availableWords.filter(
    (w) =>
      w.word.charAt(0) === correctWord.charAt(0) &&
      w.category !== correctWordObj.category
  );

  if (sameLetterDifferentCategory.length > 0) {
    let chosen =
      sameLetterDifferentCategory[
        Math.floor(Math.random() * sameLetterDifferentCategory.length)
      ];
    optionWords.push(chosen);
    // Remove the chosen option from availableWords
    availableWords = availableWords.filter((w) => w.word !== chosen.word);
  }

  // 3. Find two options that start with different letters than correctWord
  let differentLetterOptions = availableWords.filter(
    (w) => w.word.charAt(0) !== correctWord.charAt(0)
  );

  while (optionWords.length < 4 && differentLetterOptions.length > 0) {
    let chosen =
      differentLetterOptions[
        Math.floor(Math.random() * differentLetterOptions.length)
      ];

    optionWords.push(chosen);
    differentLetterOptions = differentLetterOptions.filter(
      (w) => w.word !== chosen.word
    );
  }

  if (optionWords.length < 4) {
    // If not enough different letter options, fill from any remaining available words
    while (optionWords.length < 4 && availableWords.length > 0) {
      let chosen =
        availableWords[Math.floor(Math.random() * availableWords.length)];

      optionWords.push(chosen);
      availableWords = availableWords.filter((w) => w.word !== chosen.word);
    }
  }

  // Final step: Shuffle the entire optionWords array to randomize order
  for (let i = optionWords.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    [optionWords[i], optionWords[j]] = [optionWords[j], optionWords[i]];
  }

  return optionWords;
}

export function getClues(testWords, correctWord, categoryMap) {
  const categoryId = testWords.find((w) => w.word === correctWord).category;
  const category = categoryMap[categoryId];

  // word1 is the other word from testWords from the same category
  const word1 = testWords.find(
    (w) => w.word !== correctWord && w.category === categoryId
  ).word;

  // word2 is the other word from testWords with the same starting letter
  const word2 = testWords.find(
    (w) => w.word !== correctWord && w.word.charAt(0) === correctWord.charAt(0)
  ).word;

  return {
    category,
    word1,
    word2,
    letter: correctWord.charAt(0),
  };
}

export function getFTUEData(locale='en-us', tutorInSpanish=false) {
  if (locale.toLowerCase() === 'en-us' && !tutorInSpanish) {
    return {
      testWords: [
        {
          word: "beans",
          category: 1,
        },
        {
          word: "carrot",
          category: 1,
        },
        {
          word: "box",
          category: 2,
        },
        {
          word: "crate",
          category: 2,
        },
      ],
      correctWord: "crate",
      optionWords: [
        {
          word: "cookie",
          category: 1,
        },
        {
          word: "crate",
          category: 2,
        },
        {
          word: "bag",
          category: 2,
        },
        {
          word: "bread",
          category: 1,
        },
      ],
      categoryMap: {
        1: "food",
        2: "containers",
      },
    };
  } else {
    return {
      testWords: [
        {
          word: "banano",
          category: 1,
        },
        {
          word: "coco",
          category: 1,
        },
        {
          word: "bolsa",
          category: 2,
        },
        {
          word: "caja",
          category: 2,
        },
      ],
      correctWord: "caja",
      optionWords: [
        {
          word: "carne",
          category: 1,
        },
        {
          word: "caja",
          category: 2,
        },
        {
          word: "baúl",
          category: 2,
        },
        {
          word: "bistec",
          category: 1,
        },
      ],
      categoryMap: {
        1: "comida",
        2: "contenedores",
      },
    };
  }
}